// app services

import { PrintModeService } from '../../print-mode.service';

import { IQuestionConfig } from "../../../ui-testrunner/models";
import { FormControl } from '@angular/forms';
import { ItemSetFrameworkCtrl } from './framework';
import { ItemBankUtilCtrl } from './util';
import { ItemFilterCtrl } from './item-filter';
import { ItemBankCtrl } from './item-bank';
import { ItemSetPrintViewCtrl } from './print-view';
import { TestletCtrl } from './testlets';
import { DimensionType, IAssessmentFrameworkDimensionDetail, IQuadrantConstraint, QuadrantIntersectionFunction } from '../models/assessment-framework';
import { Destroyable } from './destroyable';
import { LoginGuardService } from 'src/app/api/login-guard.service';
import {
  findPartitionForSection,
  formatPartitionSectionOption,
  sortQuadrantsBySectionInPlace,
} from '../services/quadrant-section';

export class FrameworkQuadrantCtrl implements Destroyable {

  showQuadrantConfigDetail = new FormControl();
  activeQuadrant: {id: string | number, questions: IQuestionConfig[]};
  quadrantsCrossLinks: any[] = [];
  public util = new ItemBankUtilCtrl();

  // Memoization cache for parameter lookups
  private _paramCache: {
    details?: Map<string, IAssessmentFrameworkDimensionDetail>,
    type?: Map<string, any>,
    allowedInputTypes?: Map<string, QuadrantIntersectionFunction[]>,
    inputType?: Map<string, 'text' | 'checkbox' | 'number' | null>
  } = {
    details: new Map(),
    type: new Map(),
    allowedInputTypes: new Map(),
    inputType: new Map(),
  };

  constructor(
    public frameworkCtrl:ItemSetFrameworkCtrl,
    public printMode: PrintModeService,
    public itemFilterCtrl:ItemFilterCtrl,
    public itemBankCtrl: ItemBankCtrl,
    public printViewCtrl: ItemSetPrintViewCtrl,
    public testletCtrl: TestletCtrl,
    public loginGuard: LoginGuardService
  ){
    
  }

  destroy() {

    }
  
    createQuadrantSection() {
      const partitions = this.frameworkCtrl.asmtFmrk.partitions || [];
      if (!partitions.length) {
        alert('Define assessment sections before adding quadrants.');
        return;
      }

      const listing = partitions
        .map((partition) => formatPartitionSectionOption(partition))
        .join('\n');
      const defaultSection = partitions[0];
      const pick = prompt(
        `Which section is this quadrant connected to?\n\n${listing}\n\nEnter section id:`,
        String(defaultSection.id),
      );
      if (pick === null) {
        return;
      }
      const partition = findPartitionForSection(partitions, pick.trim());
      if (!partition) {
        alert('Section id not found in current framework sections.');
        return;
      }

      const description = prompt('How would you describe the new quadrant?', 'new quadrant');
      if(description === null){return;}
      this.frameworkCtrl.asmtFmrk.quadrants.push({
        id: this.util.nextId(this.frameworkCtrl.asmtFmrk.quadrants),
        section: partition.id,
        description,
        constraints: [],
        config: {
          numItems: 1,
          discardThreshold: 100,
          triesBeforeFail: 100,
          triesBeforeFailWhole: 100,
          constraints: [],
        },
      });
      sortQuadrantsBySectionInPlace(
        this.frameworkCtrl.asmtFmrk.quadrants,
        this.frameworkCtrl.asmtFmrk.partitions,
      );
    }

  clearQuadrantQuestions(){
    this.activateQuadrantQuestions(null);
  }

  activateQuadrantQuestions(quadrantId: string | number, questions: IQuestionConfig[] = undefined) {
      this.activeQuadrant = {
        id: quadrantId,
        questions: questions !== undefined ? questions : this.getQuadrantQuestions(quadrantId),
      };
      this.itemBankCtrl.currentItemListPage = 1;
      this.itemFilterCtrl.updateItemFilter();
      this.frameworkCtrl.scrollToQuestionListing();
  }

  printQuadrantQuestions(quadrantId: string | number) {
    this.printViewCtrl.printModeQuestions = [];
    const quadrantConfig = this.getQuadrantConfigById(quadrantId);
    this.printViewCtrl.printTitle = 'Quadrant ' + quadrantId + ': ' + quadrantConfig.description;
    this.getQuadrantQuestions(quadrantId).forEach(_question => {
      const question = this.itemBankCtrl.getQuestionContent(_question);
      this.printViewCtrl.printModeQuestions.push({
        props: [
          {caption: 'Quadrant ID', val: quadrantId},
          {caption: 'Quadrant Name', val: quadrantConfig.description},
          {caption: 'Question Label', val: question.label},
        ],
        question,
      });
    });
    this.printMode.isActive = true;
  }

  computePerItemsUsedQuad(quadrant: any) {
    const deno = quadrant.questions.length;
    const nume = quadrant.numItemsUsed;
    let perc = 0;
    if (deno) {
      perc = Math.round(100 * 100 * nume / deno) / 100;
    }
    return perc + '%';
  }

  /**
   * Returns the list of questions for a given quadrant ID.
   */
  getQuadrantQuestions(quadrantId: string | number) {
    const questions: IQuestionConfig[] = [];
    const questionIdList:number[] = [];
    this.frameworkCtrl.asmtFmrk.quadrantItems.forEach(quadrantItemSet => {
      if (quadrantItemSet.id === quadrantId) {
        for (let qDef of quadrantItemSet.questions){
          questionIdList.push(+qDef.id)
        }
      }
    });
    this.itemBankCtrl.getItems().forEach(question => {
      if (questionIdList.includes(+question.id)) {
        questions.push(question);
      }
    });
    return questions;
  }

  getTestletReplacementSetting() {
    return !!this.frameworkCtrl.asmtFmrk.isTestletItemsReplaceDisabled;
  }

  /**
   * Returns the quadrant config object for a given quadrant ID.
   */
  getQuadrantConfigById(quadrantId: string | number) {
    let quadrant;
    this.frameworkCtrl.asmtFmrk.quadrants.forEach(_quadrant => {
      if (_quadrant.id === quadrantId) {
        quadrant = _quadrant;
      }
    });
    return quadrant;
  }

  getQuadrantItemsById(quadrantId: number){
    return this.frameworkCtrl.asmtFmrk?.quadrantItems?.find(qItems => qItems.id == quadrantId) ?? <any>{};
  }
    
  refreshQuadrantItems(config?:{watchQuadrantId?:number, watchItemIds?:number[]}) {
    const {watchQuadrantId, watchItemIds} = (config || {})
    this.frameworkCtrl.asmtFmrk.quadrantItems = [];
    const failedCases = [];
    // Build a paramRef map for dimension type parsing
    const paramRef: Map<string, IAssessmentFrameworkDimensionDetail> = new Map();
    const dimensions = this.frameworkCtrl.getCurrentParameters();
    dimensions.forEach((dim: IAssessmentFrameworkDimensionDetail) => {
      paramRef.set(dim.code, dim);
    });
    this.frameworkCtrl.asmtFmrk.quadrants.forEach((quadrant, quadrantIndex) => {
      const assignedItems = new Map();
      const quadrantItemsDef = {
        id: quadrant.id,
        description: quadrant.description,
        minQRequired: quadrant.config.numItems,
        questions: [],
        testletQuestions: [],
        crossLinkedItems: [],
        section: quadrant.section
      };
      if (quadrant.config.numItems > 0) {
        this.itemBankCtrl.getItems().forEach((question: any, questionIndex) => {
          const id = question.id;
          const label = question.label;
          const meta = question.meta;
          let isAllConstraintsSatisfied = true;
          const itemFailedConstraints = [];
          quadrant.constraints.forEach(constraint => {
            if(Object.keys(QuadrantIntersectionFunction).includes(constraint.quadrantIntersectionFunction)) {
              // Use sanitizeParameterValue for type-appropriate parsing
              const parsedVal = this.sanitizeParameterValue(constraint.param, meta[constraint.param]);
              let valTarget: any = null;
              let comparisonFailed = false;
              if(!this.getAllowedParamConstraintInputTypes(constraint.param).includes(constraint.quadrantIntersectionFunction)) return
              switch (constraint.quadrantIntersectionFunction) {
                case QuadrantIntersectionFunction.EQUAL:
                  valTarget = this.sanitizeParameterValue(constraint.param, constraint.targets?.[0]);
                  comparisonFailed = parsedVal !== valTarget;
                  break;

                case QuadrantIntersectionFunction.MAX:
                  valTarget = this.sanitizeParameterValue(constraint.param, constraint.max);
                  comparisonFailed = isNaN(parsedVal) || isNaN(valTarget) || parsedVal > valTarget;
                  break;

                case QuadrantIntersectionFunction.MIN:
                  valTarget = this.sanitizeParameterValue(constraint.param, constraint.min);
                  comparisonFailed = isNaN(parsedVal) || isNaN(valTarget) || parsedVal < valTarget;
                  break;

                case QuadrantIntersectionFunction.RANGE:
                  const min = this.sanitizeParameterValue(constraint.param, constraint.min);
                  const max = this.sanitizeParameterValue(constraint.param, constraint.max);
                  valTarget = [min, max];
                  comparisonFailed = isNaN(parsedVal) || isNaN(min) || isNaN(max) || parsedVal < min || parsedVal > max;
                  break;

                case QuadrantIntersectionFunction.INCLUDES:
                  valTarget = (constraint.targets || []).map(v => this.sanitizeParameterValue(constraint.param, v));
                  comparisonFailed = !valTarget?.includes(parsedVal);
                  break;

                default:
                  valTarget = (constraint.targets || []).map(v => this.sanitizeParameterValue(constraint.param, v));
                  comparisonFailed = true;
                  console.warn('Unknown quadrant intersection function:', constraint.quadrantIntersectionFunction);
                  break;
              }

              if (comparisonFailed) {
                isAllConstraintsSatisfied = false;
                itemFailedConstraints.push({
                  param: constraint.param,
                  valTarget,
                  parsedVal,
                  func: constraint.quadrantIntersectionFunction
                });
              }

            } else { // legacy handling for old constraints deprecated
              const vals = ('' + constraint.val).split(',');
              const targetVals = vals.map(this.util.numStrToBool);
              let configuredVal;
              try {
                configuredVal = this.util.numStrToBool(meta[constraint.param]);
              } catch (e) {
                // console.warn('Missing meta', label);
                configuredVal = false;
              }
              if (targetVals.indexOf(configuredVal) === -1 ) {
                isAllConstraintsSatisfied = false;
                itemFailedConstraints.push({
                  param: constraint.param, 
                  val_target: targetVals, 
                  val_item: configuredVal, 
                  val_item_raw: meta[constraint.param]
                })
              }
            }
          });

          if (isAllConstraintsSatisfied) {
            if (assignedItems.has(id)) {
              const quadrantsCrossLinks = assignedItems.get(id);
              if (quadrantsCrossLinks.length = 1) {
                quadrantItemsDef.crossLinkedItems.push({ id, label, quadrantsCrossLinks });
              }
              quadrantsCrossLinks.push(quadrant.id);
            } else {
              assignedItems.set(id, [quadrant.id]);
            }
            quadrantItemsDef.questions.push({id, label});
          }
          else {
            if (quadrant.id == watchQuadrantId && watchItemIds && watchItemIds.includes(+question.id)){
              failedCases.push({
                quadrant_id: quadrant.id,
                item_id: question.id,
                gaps: itemFailedConstraints,
              })
            }
          }
        });
        this.frameworkCtrl.asmtFmrk.quadrantItems.push(quadrantItemsDef);
      }
    });
    this.testletCtrl.updateTestletSummary();
    return failedCases;
  }
  
  createQuadrantConstraint(constraints: IQuadrantConstraint[]) {
    const param = (prompt('Parameter') || '').trim();
    const paramDetails = this.frameworkCtrl.getCurrentParameters().find(p => p.code === param);
    if(!paramDetails || this.getAllowedParamConstraintInputTypes(param).length === 0) {
      this.loginGuard.quickPopup('Parameter not found please check the parameter code exists in the framework. Or the parameter type does not support constraints.');
      return;
    }
    let intersectionFunc: string | undefined;
    const functions = this.getAllowedParamConstraintInputTypes(param);
    let promptMsg = 'Intersection Function Copy Paste one of the following:\n';
    for (const func of functions) {
      promptMsg += `- ${func}\n`;
    }
    intersectionFunc = (prompt(promptMsg) || '').trim().toUpperCase();
    if (!Object.values(QuadrantIntersectionFunction).includes(intersectionFunc as QuadrantIntersectionFunction)) {
      alert('Invalid or missing intersection function! Defaulting to EQUAL.');
      intersectionFunc = QuadrantIntersectionFunction.EQUAL;
    }
    let min: number | undefined = undefined;
    let max: number | undefined = undefined;
    let targets: (string|number)[] | undefined = undefined;
    if (intersectionFunc === QuadrantIntersectionFunction.RANGE) {
      min = Number(prompt('Enter minimum value for range:'));
      max = Number(prompt('Enter maximum value for range:'));
      if (isNaN(min)) min = 0;
      if (isNaN(max)) max = 0;
    } else if (intersectionFunc === QuadrantIntersectionFunction.INCLUDES) {
      const input = prompt('Enter comma-separated values for list:');
      targets = input ? input.split(',').map(v => v.trim()) : [];
    } else if (intersectionFunc === QuadrantIntersectionFunction.MIN) {
      min = Number(prompt('Enter minimum value:'));
      if (isNaN(min)) min = 0;
    } else if (intersectionFunc === QuadrantIntersectionFunction.MAX) {
      max = Number(prompt('Enter maximum value:'));
      if (isNaN(max)) max = 0;
    } else {
      const value = prompt('Value') || '';
      targets = [value];
    }
    const newConstraint: IQuadrantConstraint = {
      param,
      quadrantIntersectionFunction: intersectionFunc as QuadrantIntersectionFunction,
    };
    if (min !== undefined && (typeof min === 'number' || !isNaN(Number(min)))) newConstraint.min = Number(min);
    if (max !== undefined && (typeof max === 'number' || !isNaN(Number(max)))) newConstraint.max = Number(max);
    if (Array.isArray(targets)) newConstraint.targets = targets;
    this.normalizeConstraint(newConstraint)
    constraints.push(newConstraint);
  }

  ensureQuadrantIntersectionConfig() {
    const quadrants = this.frameworkCtrl.asmtFmrk.quadrants;
    if (!quadrants) return;

    quadrants.forEach(q => {
      if (!q.constraints) return;
      q.constraints.forEach(constraint => this.normalizeConstraint(constraint));
    });
  }

  normalizeConstraint(constraint: IQuadrantConstraint, quadrantIntersectionFunction?) {
    const valStr = constraint.val != null ? constraint.val.toString() : '';
    if(quadrantIntersectionFunction && this.getAllowedParamConstraintInputTypes(constraint.param).includes(quadrantIntersectionFunction)) {
      constraint.quadrantIntersectionFunction = quadrantIntersectionFunction;
    } else if (!constraint.quadrantIntersectionFunction) {
      constraint.quadrantIntersectionFunction = valStr.includes(',')
        ? QuadrantIntersectionFunction.INCLUDES
        : QuadrantIntersectionFunction.EQUAL;
    } else if(!this.getAllowedParamConstraintInputTypes(constraint.param).includes(constraint.quadrantIntersectionFunction)){
      constraint.quadrantIntersectionFunction = this.getAllowedParamConstraintInputTypes(constraint.param)[0] || QuadrantIntersectionFunction.EQUAL;
    }

    switch (quadrantIntersectionFunction ?? constraint.quadrantIntersectionFunction) {
      case QuadrantIntersectionFunction.MAX:
      case QuadrantIntersectionFunction.MIN:
      case QuadrantIntersectionFunction.RANGE: {
        if (constraint.min === undefined || isNaN(Number(constraint.min))) {
          constraint.min = 0;
        } else {
          constraint.min = Number(constraint.min);
        }
        if (constraint.max === undefined || isNaN(Number(constraint.max))) {
          constraint.max = 1;
        } else {
          constraint.max = Number(constraint.max);
        }
        break;
      }

      case QuadrantIntersectionFunction.INCLUDES:
        if (!Array.isArray(constraint.targets) || constraint.targets.length === 0) {
          constraint.targets = valStr ? valStr.split(',').map(v => this.sanitizeParameterValue(constraint.param, v)) : [];
        }
        break;
      case QuadrantIntersectionFunction.EQUAL:
      default:
        if (!Array.isArray(constraint.targets) || constraint.targets.length === 0) {
          constraint.targets = [this.sanitizeParameterValue(constraint.param, valStr)];
        }
    }

    if (!constraint.targets) {
      constraint.targets = [];
    }
    // if (constraint.val !== undefined && constraint.val !== null) {
    //   delete constraint.val; // remove old val to avoid confusion
    // }
  }

  replaceParam(constraint: IQuadrantConstraint) {
    const param = prompt('Enter new parameter name:', null)?.trim();
    if (!param) {
      return;
    }
    const paramDetails = this.getParamDetails(param);
    if(!paramDetails) {
      this.loginGuard.quickPopup('Parameter not found please check the parameter code exists in the framework.');
      return;
    }
    constraint.param = param;
    this.normalizeConstraint(constraint);
  }

  clearParamCache() {
    this._paramCache.details.clear();
    this._paramCache.type.clear();
    this._paramCache.allowedInputTypes.clear();
    this._paramCache.inputType.clear();
  }

  getParamDetails(param: string) {
    if (this._paramCache.details.has(param)) {
      return this._paramCache.details.get(param);
    }
    const found = this.frameworkCtrl.getCurrentParameters().find(p => p.code === param);
    this._paramCache.details.set(param, found);
    return found;
  }

  getParamType(param: string) {
    if (this._paramCache.type.has(param)) {
      return this._paramCache.type.get(param);
    }
    const paramDetails = this.getParamDetails(param);
    const type = paramDetails ? paramDetails.type : null;
    this._paramCache.type.set(param, type);
    return type;
  }

  getAllowedParamConstraintInputTypes(param: string): QuadrantIntersectionFunction[] {
    if (this._paramCache.allowedInputTypes.has(param)) {
      return this._paramCache.allowedInputTypes.get(param);
    }
    const paramType = this.getParamType(param);
    let allowed: QuadrantIntersectionFunction[];
    if (!paramType) {
      allowed = [];
    } else {
      switch (paramType) {
        case DimensionType.SELECT:
        case DimensionType.LABEL:
        case DimensionType.TEXT:
          allowed = [QuadrantIntersectionFunction.EQUAL, QuadrantIntersectionFunction.INCLUDES];
          break;
        case DimensionType.BINARY:
          allowed = [QuadrantIntersectionFunction.EQUAL];
          break;
        case DimensionType.NUMERIC:
          allowed = Object.values(QuadrantIntersectionFunction);
          break;
        default:
          allowed = [];
      }
    }
    this._paramCache.allowedInputTypes.set(param, allowed);
    return allowed;
  }

  getParamInputType(param: string): 'text' | 'checkbox' | 'number' | 'select' | null {
    if (this._paramCache.inputType.has(param)) {
      return this._paramCache.inputType.get(param);
    }
    const paramType = this.getParamType(param);
    let inputType;
    if (!paramType) {
      inputType = null;
    } else {
      switch (paramType) {
        case DimensionType.SELECT:
          inputType = 'select';  
        break;
        case DimensionType.TEXT:
        case DimensionType.LABEL:
          inputType = 'text';
          break;
        case DimensionType.BINARY:
          inputType = 'checkbox';
          break;
        case DimensionType.NUMERIC:
          inputType = 'number';
          break;
        default:
          inputType = null;
      }
    }
    this._paramCache.inputType.set(param, inputType);
    return inputType;
  }
  getParamSelectOptions(param: string): string[] {
    const paramDetails = this.getParamDetails(param);
    return paramDetails.config.tags.map(tag => tag.code.trim());
    }

  /**
   * Normalizes a value to match the expected type and format for a given framework parameter.
   * This is used to ensure that all comparisons and filtering logic are type-safe and consistent
   * with the parameter's definition in the assessment framework.
   * @param paramCode The parameter code to which the value should conform.
   * @param val The value to normalize.
   * @returns The normalized value, suitable for type-safe comparison.
   */
  sanitizeParameterValue(paramCode:string, val: any){
    const param = this.getParamDetails(paramCode);
    if (param){
      switch(param.type){
        case DimensionType.BINARY:
          // Accept true, 1, '1', 'true' (case-insensitive) as true
          if (val === true || val === 1 || (typeof val === 'string' && (val.trim().toLowerCase() === 'true' || val.trim() === '1' ))) return 1;
          return 0;
        case DimensionType.SELECT:  return val?.toString().trim() || ''; 
        case DimensionType.NUMERIC:
          const num = +val;
          const isNaN = ['', true, false].includes(val) || Number.isNaN(num);
          return isNaN ? null : num;
        case DimensionType.LABEL:   return val ?? ''; 
        case DimensionType.TEXT:    return val ? val.caption : '' ; 
      }
    }
    return val
  }

}
  